import { CommonModule } from '@angular/common';
import { Component, signal } from '@angular/core';
import { ReactiveFormsModule, type FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '@infra-adapters/services/auth.service';
import { PermissionsService } from '@infra-adapters/services/permissions.service';
import { ToastService } from '@infra-adapters/services/toast.service';
import { ChangePasswordFormFactory, type IChangePasswordFormInput } from '@infra-adapters/forms/change-password';
import { AuthBaseLayoutComponent } from '@infra-ui/components/auth/auth-base-layout/auth-base-layout.component';
import { COREUI_BUTTON_IMPORTS, COREUI_FORM_IMPORTS } from '@utils/coreui';

@Component({
  selector: 'app-force-change-password',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AuthBaseLayoutComponent,
    ...COREUI_FORM_IMPORTS,
    ...COREUI_BUTTON_IMPORTS,
  ],
  templateUrl: './force-change-password.component.html',
})
export class ForceChangePasswordComponent {
  isLoading = signal(false);
  passwordForm!: FormGroup<IChangePasswordFormInput>;

  constructor(
    private authService: AuthService,
    private permissionsService: PermissionsService,
    private toast: ToastService,
    private formFactory: ChangePasswordFormFactory,
    private router: Router,
  ) {
    this.passwordForm = this.formFactory.generateForm();
  }

  onSubmit(): void {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }
    this.isLoading.set(true);
    const { current_password, new_password } = this.passwordForm.value;
    this.authService.changeMyPassword(current_password!, new_password!).subscribe({
      next: () => {
        this.isLoading.set(false);
        // Forzar re-login: el JWT vigente aún tiene must_change_password=true.
        this.authService.cleanSession();
        this.permissionsService.clear();
        this.toast.success('Contraseña actualizada. Ingresa con tu nueva contraseña.');
        this.router.navigate(['/login']);
      },
      error: (err) => {
        this.isLoading.set(false);
        const applied = this.formFactory.applyBackendErrors(err, this.passwordForm);
        if (!applied) {
          this.toast.error(err?.error?.message || 'No se pudo actualizar la contraseña.');
        }
      },
    });
  }

  getError(field: keyof IChangePasswordFormInput): string | null {
    return this.formFactory.getErrorMessage(field, this.passwordForm);
  }

  isValid(field: keyof IChangePasswordFormInput): boolean {
    return this.formFactory.isControlValid(field, this.passwordForm);
  }

  isInvalid(field: keyof IChangePasswordFormInput): boolean {
    return this.formFactory.isControlInvalid(field, this.passwordForm);
  }
}
